/**
 * Per-page `<head>` metadata for the static marketing surface (GitHub
 * issue — "Create React components for static marketing pages using
 * vite-react-ssg", Requirement #2: "Each page component must include SEO
 * metadata tags (title, description, Open Graph, Twitter cards) using
 * react-helmet-async or equivalent").
 *
 * `vite-react-ssg` (v0.9.1, already a dependency — see `../../../vite.config.ts`)
 * ships with built-in `react-helmet-async` support: it wraps the whole
 * route tree in a `HelmetProvider` and, during `vite-react-ssg build`,
 * extracts each page's collected `<Helmet>` tags directly into that
 * route's server-rendered `<head>` — no extra provider wiring or
 * `getSiteMap`/`onFinished` plumbing is required on our side; simply
 * rendering `<Helmet>` from any statically-generated route is enough for
 * the emitted HTML in `dist/<path>.html` to carry real, per-route
 * `<title>`/meta/OG/Twitter tags (Requirement #3, and the acceptance
 * criterion "static HTML files exist for all marketing routes with
 * correct SEO metadata in the head").
 *
 * `title`/`description` fall back to sane site-wide defaults if a caller
 * passes an empty/missing value (edge case from this issue's tech spec:
 * "Missing or incomplete SEO metadata props cause fallback defaults"),
 * rather than shipping an empty `<title>`/meta description.
 *
 * NOTE: `apps/web/package.json` pins `react-helmet-async@^1.3.0`, not the
 * newer `^2.x`. `vite-react-ssg@0.9.1` itself depends on
 * `react-helmet-async@^1.3.0` (see its `package.json`) and its build step
 * runs the compiled SSG entry directly under Node's own ESM loader
 * (`node .../vite-react-ssg.js build`), which cannot resolve `import {
 * Helmet } from 'react-helmet-async'` as a named export against `2.x`'s
 * CJS output (`vite-react-ssg build` fails with `SyntaxError: Named
 * export 'Helmet' not found`). `1.x`'s CJS build does not have this
 * problem and exposes the exact same `Helmet`/`HelmetProvider` API used
 * here.
 */
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

export const SITE_NAME = 'Hero Hire AI';
// Canonical host (GH issue #572/#300 — "Meta tag cleanup on live site ...
// Canonical consistency"): `/how-it-works` was resolving on `www` while
// the homepage resolved on non-`www`. `herohire.ai` (no `www`) is the one
// canonical host, enforced site-wide by `public/_redirects` (301
// `www.herohire.ai` -> `herohire.ai`) — every `<link rel="canonical">`
// below must always point at this same non-www origin.
export const SITE_URL = 'https://herohire.ai';
// Renamed from `Hero Product Screenshot.png` (GH issue #572/#300, item
// #5 — "Rename og:image file"): the old filename had spaces, which URL-
// encode to `%20` in the rendered `og:image`/`twitter:image` tags and
// break some social-card scrapers. The asset itself lives at
// `apps/web/public/hero-product-screenshot.png` (lowercase, hyphenated,
// no spaces) and is served at this exact path.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/hero-product-screenshot.png`;
export const DEFAULT_TITLE = `${SITE_NAME} — AI-Powered Recruitment Platform`;
export const DEFAULT_DESCRIPTION =
  'Hero Hire AI screens, ranks, and shortlists candidates automatically with AI.';

export interface SeoHeadProps {
  /** Rendered as `<title>` and `og:title`/`twitter:title`. */
  title?: string;
  /** Rendered as the meta description and `og:description`/`twitter:description`. */
  description?: string;
  /** Route path (e.g. `/how-it-works`), used to build the canonical/`og:url`. */
  path: string;
  /** Open Graph object type. Defaults to `website` for all marketing pages. */
  type?: 'website' | 'article';
}

function canonicalUrl(path: string): string {
  const normalized = path === '/' ? '' : path;
  return `${SITE_URL}${normalized}`;
}

/**
 * Shared SEO/Open Graph/Twitter card `<head>` tags for a single
 * statically-generated marketing route. Render once, near the top of the
 * page component (see `MarketingLayout.tsx` and each `src/pages/marketing/*`
 * page).
 */
export default function SeoHead({
  title,
  description,
  path,
  type = 'website',
}: SeoHeadProps): JSX.Element {
  const { i18n } = useTranslation();
  const resolvedTitle = title && title.trim().length > 0 ? title : DEFAULT_TITLE;
  const resolvedDescription =
    description && description.trim().length > 0 ? description : DEFAULT_DESCRIPTION;
  const url = canonicalUrl(path);

  return (
    <Helmet htmlAttributes={{ lang: i18n.language || 'en' }}>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={url} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
    </Helmet>
  );
}
